import React, { useState, useEffect } from 'react';
import { Modal, Image, Button, Form, Loader, Dimmer, Icon } from 'semantic-ui-react';
import { useGetAvDataQuery, useReviewIdentityMutation } from './chatSessionApiSlice';

const STATUS_BADGE = {
  VERIFIED: { label: '✓ 신원확인', background: '#E8F5E9', color: '#2E7D32', border: '1px solid #A5D6A7' },
  FLAGGED:  { label: '⚑ 신고됨',   background: '#FFEBEE', color: '#C62828', border: '1px solid #FFCDD2' },
  PENDING:  { label: '● 검토 전',   background: '#F5F5F5', color: '#757575', border: '1px solid #E0E0E0' },
};

const IdentityReviewModal = ({ sessionId, open, onClose, onReviewSuccess }) => {
  const [flagMode, setFlagMode] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const { data: avData, isLoading, isFetching } = useGetAvDataQuery(sessionId, { skip: !open || !sessionId });
  const [reviewIdentity, { isLoading: isSubmitting }] = useReviewIdentityMutation();

  useEffect(() => {
    if (open) {
      setFlagMode(false);
      setFlagReason('');
      setSubmitError(null);
    }
  }, [open, sessionId]);

  useEffect(() => {
    if (avData?.identity_verified_status === 'FLAGGED' && avData?.identity_flag_reason) {
      setFlagReason(avData.identity_flag_reason);
    }
  }, [avData]);

  const handleVerify = async () => {
    setSubmitError(null);
    const result = await reviewIdentity({ sessionId, status: 'VERIFIED' });
    if (result.error) { setSubmitError('저장에 실패했습니다. 다시 시도해주세요.'); return; }
    onClose();
    onReviewSuccess();
  };

  const handleFlagSubmit = async () => {
    if (!flagReason.trim()) return;
    setSubmitError(null);
    const result = await reviewIdentity({ sessionId, status: 'FLAGGED', flag_reason: flagReason });
    if (result.error) { setSubmitError('저장에 실패했습니다. 다시 시도해주세요.'); return; }
    onClose();
    onReviewSuccess();
  };

  const handleClose = () => {
    setFlagMode(false);
    setFlagReason('');
    setSubmitError(null);
    onClose();
  };

  const trainee = avData?.trainee;
  const status = avData?.identity_verified_status || 'PENDING';
  const badge = STATUS_BADGE[status] || STATUS_BADGE.PENDING;
  const isFlagged = status === 'FLAGGED' && !!avData?.identity_flag_reason;

  return (
    <Modal open={open} onClose={handleClose} size="large">
      {/* ── Header ── */}
      <Modal.Header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>신원 확인{trainee ? ` — ${trainee.full_name}` : ''}</span>
          {!isLoading && !isFetching && (
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
              letterSpacing: '0.03em', ...badge,
            }}>
              {badge.label}
            </span>
          )}
        </div>
        <Icon name="close" style={{ cursor: 'pointer', fontSize: 16 }} onClick={handleClose} />
      </Modal.Header>

      {/* ── Body ── */}
      <Modal.Content style={{ padding: 0, minHeight: isFlagged && !flagMode ? 500 : 420, transition: 'min-height 0.35s ease' }}>
        {isLoading || isFetching ? (
          <div style={{ position: 'absolute', inset: 0 }}>
            <Dimmer active inverted><Loader>불러오는 중...</Loader></Dimmer>
          </div>
        ) : (
          <div style={{ display: 'flex', minHeight: isFlagged && !flagMode ? 500 : 420, transition: 'min-height 0.35s ease' }}>

            {/* Left panel */}
            <div style={{
              width: 340, minWidth: 340,
              padding: '24px 24px',
              borderRight: '1px solid #f0f0f0',
              display: 'flex', flexDirection: 'column', alignItems: 'stretch',
            }}>
              {trainee?.avatar_url ? (
                <Image src={trainee.avatar_url} circular size="small" centered style={{ marginBottom: 12 }} />
              ) : (
                <div style={{
                  width: 80, height: 80, borderRadius: '50%', background: '#c8c8cc',
                  margin: '0 auto 12px', flexShrink: 0,
                }} />
              )}
              <div style={{ textAlign: 'center', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>
                {trainee?.full_name}
              </div>

              {avData?.audio_url && (
                <div style={{ width: '100%', marginBottom: 16 }}>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <audio controls src={avData.audio_url} style={{ width: '100%' }} />
                </div>
              )}

              {submitError && (
                <div style={{ color: '#C0392B', fontSize: 12, marginBottom: 10, textAlign: 'center' }}>
                  {submitError}
                </div>
              )}

              {/* Flag reason — instantly removed when flagMode activates */}
              {isFlagged && !flagMode && (
                <div style={{
                  width: '100%', marginBottom: 14,
                  background: '#FFF6F6', border: '1px solid #FFCDD2', borderRadius: 10,
                  padding: '10px 12px', fontSize: 12,
                  wordBreak: 'break-word', overflowWrap: 'break-word',
                }}>
                  <div style={{ color: '#C62828', fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', marginBottom: 4 }}>
                    ⚑ 신고 사유
                  </div>
                  <div style={{ color: '#444', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
                    {avData?.identity_flag_reason}
                  </div>
                </div>
              )}

              {/* Action area — fixed min-height so flagMode toggle doesn't resize panel */}
              <div style={{ width: '100%', minHeight: 130 }}>
                {!flagMode ? (
                  <>
                    <Button
                      fluid
                      loading={isSubmitting}
                      onClick={handleVerify}
                      style={{
                        marginBottom: 8, background: '#34C759', color: '#fff',
                        fontWeight: 600, borderRadius: 10,
                      }}
                    >
                      ✓ 신원 확인
                    </Button>
                    <Button
                      fluid
                      onClick={() => setFlagMode(true)}
                      style={{
                        background: '#FFF0EF', color: '#FF3B30',
                        border: '1.5px solid #FF3B30', fontWeight: 600, borderRadius: 10,
                      }}
                    >
                      ⚑ 의심 신고
                    </Button>
                  </>
                ) : (
                  <Form>
                    <Form.TextArea
                      placeholder="신고 사유를 입력하세요"
                      value={flagReason}
                      onChange={(e, { value }) => setFlagReason(value)}
                      rows={3}
                      style={{ resize: 'none', wordBreak: 'break-word', borderRadius: 8 }}
                    />
                    <Button
                      fluid color="red"
                      onClick={handleFlagSubmit}
                      disabled={!flagReason.trim()}
                      loading={isSubmitting}
                      style={{ marginTop: 8, borderRadius: 10 }}
                    >
                      신고 확인
                    </Button>
                    <Button
                      basic fluid
                      onClick={() => { setFlagMode(false); setFlagReason(avData?.identity_flag_reason || ''); }}
                      style={{ marginTop: 6, borderRadius: 10 }}
                    >
                      취소
                    </Button>
                  </Form>
                )}
              </div>
            </div>

            {/* Right panel */}
            <div style={{ flex: 1, padding: '22px 20px', overflow: 'hidden' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#86868b', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 12 }}>
                웹캠 캡처 · {avData?.webcam_captures?.length ?? 0}장
              </div>
              {avData?.webcam_captures?.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, overflowY: 'auto', maxHeight: 370 }}>
                  {avData.webcam_captures.map((capture, idx) => (
                    <div key={idx} style={{ textAlign: 'center' }}>
                      <img
                        src={capture.url}
                        alt={`webcam-${idx}`}
                        style={{ width: '100%', borderRadius: 8, display: 'block' }}
                      />
                      <div style={{ fontSize: 10, color: '#86868b', marginTop: 3 }}>
                        {new Date(capture.timestamp).toLocaleTimeString('ko-KR', {
                          hour: '2-digit', minute: '2-digit', second: '2-digit',
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: '#86868b', fontSize: 13 }}>웹캠 캡처 없음</div>
              )}
            </div>

          </div>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default IdentityReviewModal;
